import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import api, { formatINR } from "../lib/api";
import { MaskedLines, Reveal } from "../components/Reveal";

export default function Localities() {
  const [localities, setLocalities] = useState(null);

  useEffect(() => {
    document.title = "Locality Guides — Gurgaon Estates";
    api.get("/localities").then(({ data }) => setLocalities(data.localities)).catch(() => setLocalities([]));
  }, []);

  return (
    <div className="min-h-screen pt-28" data-testid="localities-page">
      <div className="mx-auto max-w-[1600px] px-6 pb-32 lg:px-12">
        <p className="font-mono text-[10px] uppercase tracking-[0.4em] text-gold">The Atlas</p>
        <MaskedLines
          lines={["Know the address", "before the home."]}
          className="mt-4 max-w-4xl font-serif text-5xl font-light leading-[1.02] tracking-tight text-bone sm:text-7xl"
        />
        <Reveal delay={0.3}>
          <p className="mt-6 max-w-xl text-sm font-light leading-relaxed text-smoke">
            Every locality we cover, decoded — the stock, the medians, the character. Choose your ground first;
            the right residence follows.
          </p>
        </Reveal>

        <div className="mt-16 grid grid-cols-1 gap-8 sm:grid-cols-2 xl:grid-cols-3">
          {(localities || []).map((l, i) => (
            <Reveal key={l.slug} delay={Math.min(i, 5) * 0.07}>
              <Link
                to={`/localities/${l.slug}`}
                data-testid={`locality-card-${l.slug}`}
                className="group block border border-white/10 bg-obsidian transition-colors duration-500 hover:border-gold/50"
              >
                <div className="relative aspect-[16/9] overflow-hidden">
                  <img
                    src={l.image}
                    alt={l.name}
                    loading={i > 2 ? "lazy" : "eager"}
                    className="h-full w-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-[1.06]"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/10 to-transparent" />
                  <span className="absolute right-4 top-4 bg-black/70 px-3 py-1 font-mono text-[10px] uppercase tracking-[0.25em] text-gold backdrop-blur-md">
                    {l.listing_count} live
                  </span>
                  <h2 className="absolute bottom-4 left-4 font-serif text-3xl font-light text-bone">{l.name}</h2>
                </div>
                <div className="p-5">
                  <p className="text-sm font-light leading-relaxed text-smoke">{l.short}</p>
                  <div className="mt-4 flex items-center justify-between border-t border-white/10 pt-4">
                    <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-smoke">
                      {l.median_sale ? `Median ${formatINR(l.median_sale)}` : l.median_rent ? `Lease ${formatINR(l.median_rent, "monthly")}` : "New coverage"}
                    </span>
                    <span className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.2em] text-gold transition-transform group-hover:translate-x-1">
                      Guide <ArrowRight size={12} />
                    </span>
                  </div>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </div>
    </div>
  );
}
